const BILLING_SECTION_SELECTOR = ".js-exhibitor-billing-address"
const BILLING_TOGGLE_SELECTOR = ".js-exhibitor-billing-address-toggle"

const COMPANY_FIELD_IDS = {
  address: "exhibitor_adresse",
  zip: "exhibitor_code_postal",
  city: "exhibitor_ville",
  country: "exhibitor_pays",
}

const BILLING_FIELD_NAMES = {
  address: "exhibitor[billing_address]",
  zip: "exhibitor[billing_zip_code]",
  city: "exhibitor[billing_city]",
  country: "exhibitor[billing_country]",
}

const DEFAULT_COUNTRY_FR = "France"

const normalizeFieldValue = (value) => String(value || "").trim()

const addressDisplayCountry = (country) => {
  const normalized = normalizeFieldValue(country)
  if (!normalized) return ""
  if (normalized.localeCompare(DEFAULT_COUNTRY_FR, "fr", { sensitivity: "accent" }) === 0) return ""
  return normalized
}

const findField = (root, name) => {
  const form = root.closest("form")
  if (form) {
    const inForm = form.querySelector(`[name="${name}"]`)
    if (inForm) return inForm
  }
  return document.querySelector(`[name="${name}"]`)
}

const readCompanyAddress = (section) => {
  const root = section.closest("form") || document
  return {
    address: normalizeFieldValue(root.querySelector(`#${CSS.escape(COMPANY_FIELD_IDS.address)}`)?.value),
    zip: normalizeFieldValue(root.querySelector(`#${CSS.escape(COMPANY_FIELD_IDS.zip)}`)?.value),
    city: normalizeFieldValue(root.querySelector(`#${CSS.escape(COMPANY_FIELD_IDS.city)}`)?.value),
    country: normalizeFieldValue(root.querySelector(`#${CSS.escape(COMPANY_FIELD_IDS.country)}`)?.value),
  }
}

const readBillingFields = (section) => ({
  address: findField(section, BILLING_FIELD_NAMES.address),
  zip: findField(section, BILLING_FIELD_NAMES.zip),
  city: findField(section, BILLING_FIELD_NAMES.city),
  country: findField(section, BILLING_FIELD_NAMES.country),
})

const setInputValue = (input, value) => {
  if (!input) return
  input.value = value
  input.classList.toggle("form__input--filled", normalizeFieldValue(value) !== "")
}

const formatBillingSummary = ({ address, zip, city, country }) => {
  const cityLine = [zip, city].filter(Boolean).join(", ")
  const countryLine = addressDisplayCountry(country)
  return {
    address,
    city: cityLine,
    country: countryLine ? countryLine.toUpperCase() : "",
  }
}

const updateBillingSummaryDisplay = (section, company) => {
  const summary = formatBillingSummary(company)
  const addressLine = section.querySelector('[data-billing-summary-line="address"]')
  const cityLine = section.querySelector('[data-billing-summary-line="city"]')
  const countryLine = section.querySelector('[data-billing-summary-line="country"]')

  if (addressLine) addressLine.textContent = summary.address
  if (cityLine) cityLine.textContent = summary.city
  if (countryLine) {
    if (summary.country) {
      countryLine.textContent = summary.country
      countryLine.hidden = false
    } else {
      countryLine.textContent = ""
      countryLine.hidden = true
    }
  }
}

const clearBillingFields = (section) => {
  const billing = readBillingFields(section)
  setInputValue(billing.address, "")
  setInputValue(billing.zip, "")
  setInputValue(billing.city, "")
  setInputValue(billing.country, "")
}

const syncBillingSummaryFromCompany = (section) => {
  const company = readCompanyAddress(section)
  updateBillingSummaryDisplay(section, company)
}

const updateBillingToggleContent = (section, mode) => {
  const toggle = section.querySelector(BILLING_TOGGLE_SELECTOR)
  const templateKey = mode === "same" ? "use-different" : "use-company"
  const template = section.querySelector(`template[data-billing-toggle-template="${templateKey}"]`)
  if (!toggle || !template) return

  toggle.replaceChildren(...template.content.cloneNode(true).childNodes)
}

const setBillingMode = (section, mode) => {
  const toggle = section.querySelector(BILLING_TOGGLE_SELECTOR)
  const fields = section.querySelector("[data-billing-fields]")
  const summary = section.querySelector("[data-billing-summary]")
  const sameAsCompany = mode === "same"

  if (toggle) {
    toggle.dataset.billingMode = mode
    toggle.setAttribute("aria-expanded", sameAsCompany ? "false" : "true")
  }

  if (fields) fields.hidden = sameAsCompany
  if (summary) {
    if (sameAsCompany) {
      summary.removeAttribute("hidden")
    } else {
      summary.setAttribute("hidden", "hidden")
    }
  }

  updateBillingToggleContent(section, mode)

  if (sameAsCompany) {
    clearBillingFields(section)
    syncBillingSummaryFromCompany(section)
  }
}

const initBillingSection = (section) => {
  const initialMode = section.dataset.billingInitialMode === "same" ? "same" : "different"
  setBillingMode(section, initialMode)

  const form = section.closest("form") || document
  Object.values(COMPANY_FIELD_IDS).forEach((fieldId) => {
    const input = form.querySelector(`#${CSS.escape(fieldId)}`)
    if (!input || input.dataset.billingCompanyListener === "true") return

    input.dataset.billingCompanyListener = "true"
    const syncIfSameAsCompany = () => {
      const toggle = section.querySelector(BILLING_TOGGLE_SELECTOR)
      if (toggle?.dataset.billingMode === "same") syncBillingSummaryFromCompany(section)
    }
    input.addEventListener("input", syncIfSameAsCompany)
    input.addEventListener("change", syncIfSameAsCompany)
  })
}

let billingDelegationBound = false

const bindBillingToggleDelegation = () => {
  if (billingDelegationBound) return
  billingDelegationBound = true

  document.addEventListener("click", (event) => {
    const toggle = event.target.closest(BILLING_TOGGLE_SELECTOR)
    if (!toggle) return

    const section = toggle.closest(BILLING_SECTION_SELECTOR)
    if (!section) return

    event.preventDefault()
    const nextMode = toggle.dataset.billingMode === "same" ? "different" : "same"
    setBillingMode(section, nextMode)
  })
}

const initExhibitorBillingAddress = () => {
  bindBillingToggleDelegation()
  document.querySelectorAll(BILLING_SECTION_SELECTOR).forEach(initBillingSection)
}

export { initExhibitorBillingAddress }
